import React from 'react';
import {Container} from 'react-bootstrap';
import {CartToggleButton} from 'shared/components/Header/CartToggleButton';
import {DashboardButton} from 'shared/components/Header/DashboardButton';
import {SearchBar} from 'shared/components/Header/SearchBar';
import {useJobContext} from 'contexts/JobContext';
import {CBCImage} from 'shared/components/Image';
import {isDeviceSmall, isDeviceSmall936} from 'shared/helpers/DeviceSize';
import {useGetConfigurationQuery} from 'store/customer/appSlice';
import {User, UserType} from 'components/customer/Auth/store/userSlice';
import styled from 'styled-components';
import {MenuRenderer} from 'shared/components/HeaderMenu';
import {FloatingButtons} from 'shared/components/Header/RoomPlanner/FloatingButtons';
import {ConfirmationContainer} from 'components/customer/Confirmation';
import {useIsConfirmationPage} from 'components/customer/Confirmation/helpers/useIsConfirmationPage';
import ChatSnippet from 'shared/components/Header/ChatSnippet';
import {useAppSelector} from 'store/customer';
import {selectApplicationLogo} from 'store/customer/brandingSlice';
import {shallowEqual} from 'react-redux';
import {useRoomPreview} from 'components/customer/RoomPlanner/components/helpers/RoomPreview';
import {matchPath, useLocation} from 'react-router-dom';
import useUI from 'shared/useUI';
import {SidebarStatus} from 'store/customer/UISlice';

const HomeLink = styled.a`
    font-size: 12px;
    font-weight: bold;
    padding: 0 20px;
`;

export const Header = ({
    visible,
    userProfile,
}: {
    visible: boolean;
    userProfile?: Partial<User>;
}) => {
    const isSmallDevice = isDeviceSmall();
    const isSmallDevice936 = isDeviceSmall936();
    const jobContext = useJobContext() as {expiredJobPrice?: boolean};
    const {data: configuration, isLoading: isConfigurationLoading} =
        useGetConfigurationQuery();

    const {isConfirmationPage} = useIsConfirmationPage();
    const logo = useAppSelector(selectApplicationLogo, shallowEqual);
    const isRoomPreview = useRoomPreview();
    const location = useLocation();
    const {leftSidebar, rightSidebar} = useUI();

    const isProductPage = !!matchPath(
        '/v2/job/:jobId/room/:roomId/product',
        location.pathname
    );
    const bothSidebarsOpen =
        leftSidebar === SidebarStatus.OPEN &&
        rightSidebar === SidebarStatus.OPEN;

    return (
        <>
            {!isConfigurationLoading && configuration?.environmentName ? (
                <div className="top_color_strip" />
            ) : null}

            <header>
                <section
                    style={{
                        marginTop: visible ? '0' : '-70px',
                        transition: 'margin-top 200ms',
                        zIndex: jobContext?.expiredJobPrice ? '1100' : 'auto', // show topbar only when job prices are expired under a modal
                    }}
                    className="header-contents">
                    <div>
                        <DashboardButton />
                    </div>
                    <ConfirmationContainer
                        className="main-header"
                        $clamp={!isSmallDevice && isConfirmationPage}>
                        <Container fluid={!isSmallDevice && isConfirmationPage}>
                            <div className="header-grid">
                                <div className="logo">
                                    {logo ? (
                                        <a href="/v2">
                                            <CBCImage
                                                id="manu_logo"
                                                src={logo}
                                                alt={
                                                    userProfile?.manufacturerName
                                                }
                                                title={
                                                    userProfile?.manufacturerName
                                                }
                                                data-cy="header-logo"
                                                localDirectory={!logo}
                                            />
                                        </a>
                                    ) : null}

                                    {!isRoomPreview && !isSmallDevice936 ? (
                                        <HomeLink
                                            data-cy="header-home-link"
                                            href="/v2">
                                            Homepage
                                        </HomeLink>
                                    ) : null}
                                </div>

                                {/* NOTE:: Remove after beta is done */}
                                {isSmallDevice ? null : (
                                    <div className="notification-bell-container" />
                                )}
                                {/* NOTE:: Remove after beta is done */}
                                {!isConfigurationLoading &&
                                configuration?.environmentName ? (
                                    <div
                                        className={`info${
                                            isProductPage && bothSidebarsOpen
                                                ? ' info-sidebars-open'
                                                : ''
                                        }`}>
                                        YOU&apos;RE IN{' '}
                                        {configuration.environmentName.toUpperCase()}
                                    </div>
                                ) : null}
                                {!isRoomPreview && (
                                    <div className="main-menu">
                                        <MenuRenderer />
                                    </div>
                                )}
                                {!isRoomPreview && (
                                    <div className="searchContainer">
                                        <SearchBar />
                                    </div>
                                )}
                            </div>
                        </Container>
                    </ConfirmationContainer>
                    <div>
                        <CartToggleButton />
                        <FloatingButtons />
                    </div>
                </section>
            </header>

            {userProfile?.user_type == UserType.CUSTOMER &&
            userProfile?.chat_snippet ? (
                <ChatSnippet snippet={userProfile?.chat_snippet} />
            ) : null}
        </>
    );
};
